/**
 * useTtsCaptions - Hook for rendering word-level TTS captions
 * 
 * Fetches whisper JSON from backend, syncs to audio.currentTime via rAF,
 * and renders sliding window of words to #tts-captions div.
 */

import { useEffect, useRef } from 'react'
import i18next from 'i18next'
import { useAudioStore, type WhisperWord } from '@/stores/audioStore'
import { audioService } from '@/services/audio'
import { config } from '@/config'

const MAX_RETRIES = 5
const RETRY_DELAY_MS = 500

export function useTtsCaptions(): void {
  const whisperUrl = useAudioStore((state) => state.whisperUrl)
  const whisperWords = useAudioStore((state) => state.whisperWords)
  const whisperComplete = useAudioStore((state) => state.whisperComplete)
  const audioStatus = useAudioStore((state) => state.status)
  
  const rafIdRef = useRef<number | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const hasScrolledRef = useRef<boolean>(false)
  const lastRenderUpToRef = useRef<number>(0)

  // Fetch whisper data as FALLBACK (only when whisperWords.length === 0, e.g., after reconnect)
  useEffect(() => {
    if (!whisperUrl) return
    if (whisperWords.length > 0) return  // Already have data from socket push

    // Reset scroll tracking on new whisper URL
    hasScrolledRef.current = false
    lastRenderUpToRef.current = 0

    const controller = new AbortController()
    abortControllerRef.current = controller

    const fetchWhisper = async (retries = 0): Promise<void> => {
      try {
        // Resolve relative URL to absolute (fix for phone view hitting Vite dev server)
        const origin = new URL(config.apiUrl).origin
        const resolvedUrl = whisperUrl.startsWith('/') ? origin + whisperUrl : whisperUrl
        const response = await fetch(resolvedUrl, { signal: controller.signal })
        if (!response.ok) {
          throw new Error(i18next.t('game.httpErrorStatus', { status: response.status }))
        }

        const data = await response.json() as { words: WhisperWord[]; complete: boolean }
        useAudioStore.getState().setWhisperData(data.words, data.complete)

        // Retry if incomplete and retries left
        if (!data.complete && retries < MAX_RETRIES) {
          setTimeout(() => {
            if (!controller.signal.aborted) {
              fetchWhisper(retries + 1)
            }
          }, RETRY_DELAY_MS)
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return
        console.error('[useTtsCaptions] Fetch error (fallback):', err)
      }
    }

    fetchWhisper()

    return () => {
      controller.abort()
      abortControllerRef.current = null
    }
  }, [whisperUrl, whisperWords.length])

  // Render loop via requestAnimationFrame
  useEffect(() => {
    if (whisperWords.length === 0) {
      return
    }

    const render = () => {
      const div = document.getElementById('tts-captions')
      if (!div) {
        rafIdRef.current = requestAnimationFrame(render)
        return
      }

      const audio = audioService.getElement()
      const currentTime = audio.currentTime
      const isPlaying = audioStatus === 'playing'

      // Binary search for current word
      let currentIndex = -1
      if (isPlaying) {
        for (let i = 0; i < whisperWords.length; i++) {
          if (currentTime >= whisperWords[i].start && currentTime <= whisperWords[i].end) {
            currentIndex = i
            break
          }
        }
      }

      // Read data-attributes
      const windowSize = parseInt(div.dataset.words || '5', 10)
      const highlightClass = div.dataset.highlightClass || 'text-primary font-bold'
      const mode = div.dataset.mode || 'sliding'

      if (mode === 'prompter') {
        // Determine how many words to render
        let renderUpTo: number
        if (audioStatus === 'paused') {
          // Freeze prompter at last known position
          renderUpTo = lastRenderUpToRef.current
        } else if (!isPlaying) {
          // idle/error/skipped — show all words
          renderUpTo = whisperWords.length
        } else if (currentIndex !== -1) {
          // Normal progression — word by word
          renderUpTo = currentIndex + 1
        } else {
          // playing=true but idx=-1 (gap in whisper data)
          // Keep last known state, don't flash ALL words
          renderUpTo = Math.max(1, lastRenderUpToRef.current)
        }
        lastRenderUpToRef.current = renderUpTo

        const spans = whisperWords.slice(0, renderUpTo).map((word, idx) => {
          const isCurrent = isPlaying && idx === currentIndex
          const className = isCurrent ? highlightClass : ''
          const breakTag = word.break ? '</p><p>' : ''
          return `${breakTag}<span class="${className}">${word.word} </span>`
        })

        // Check scroll position BEFORE innerHTML changes content
        const wasNearBottom = div.scrollTop + div.clientHeight >= div.scrollHeight - 30

        div.innerHTML = `<p>${spans.join('')}</p>`

        // Scroll based on state BEFORE innerHTML
        if (wasNearBottom || !hasScrolledRef.current) {
          div.scrollTop = div.scrollHeight
          hasScrolledRef.current = true
        }
      } else {
        // Sliding mode: current word in center of window
        let startIdx = 0
        let endIdx: number

        if (isPlaying && currentIndex !== -1) {
          const halfWindow = Math.floor(windowSize / 2)
          startIdx = Math.max(0, currentIndex - halfWindow)
          endIdx = Math.min(whisperWords.length, startIdx + windowSize)
          
          // Adjust if at end
          if (endIdx - startIdx < windowSize) {
            startIdx = Math.max(0, endIdx - windowSize)
          }
        } else {
          // No current word - show first N words
          endIdx = Math.min(windowSize, whisperWords.length)
        }

        // Render words
        const spans = whisperWords.slice(startIdx, endIdx).map((word, idx) => {
          const globalIdx = startIdx + idx
          const isCurrent = isPlaying && globalIdx === currentIndex
          const className = isCurrent ? highlightClass : ''
          return `<span class="${className}">${word.word}</span>`
        })

        div.innerHTML = spans.join(' ')
      }

      if (isPlaying) {
        rafIdRef.current = requestAnimationFrame(render)
      }
    }

    // Start render loop if playing, or render static state once if not playing
    if (audioStatus === 'playing') {
      rafIdRef.current = requestAnimationFrame(render)
    } else if (audioStatus === 'resumed' || audioStatus === 'error' || audioStatus === 'loading' || audioStatus === 'paused' || audioStatus === 'skipped') {
      // Render static captions (all words, no highlight) when audio not playing
      render()
    }

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [audioStatus, whisperWords, whisperComplete])
}
